import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, 
  FileText, 
  Calendar, 
  User, 
  Download, 
  ExternalLink, 
  Eye, 
  ArrowUpDown, 
  Filter, 
  X, 
  History, 
  CreditCard 
} from 'lucide-react';
import Modal from '../components/Modal';
import './FormPage.css';
import { API_BASE_URL } from '../global';

export default function RetroactiveQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('recente');

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);

  // Modal de pré-visualização rápida do PDF
  const [previewQuote, setPreviewQuote] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedYear, startDate, endDate, sortBy, itemsPerPage]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/orcamentos_retroativos.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setQuotes(data);
      } else {
        setQuotes([]);
      }
    } catch (error) {
      console.error('Erro ao buscar orçamentos retroativos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Extrair lista de anos disponíveis para filtro rápido
  const availableYears = useMemo(() => {
    const yearsSet = new Set();
    quotes.forEach(q => {
      if (q.data_venda && q.data_venda.length >= 4) {
        const yr = q.data_venda.substring(0, 4);
        if (/^\d{4}$/.test(yr)) {
          yearsSet.add(yr);
        }
      } else if (q.data_venda_str && q.data_venda_str.includes('/')) {
        const parts = q.data_venda_str.split('/');
        if (parts.length === 3 && /^\d{4}$/.test(parts[2])) {
          yearsSet.add(parts[2]);
        }
      }
    });
    return Array.from(yearsSet).sort((a, b) => Number(b) - Number(a));
  }, [quotes]);

  const formatDateDisplay = (quote) => {
    if (quote.data_venda_str) return quote.data_venda_str;
    if (quote.data_venda) {
      const parts = quote.data_venda.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return quote.data_venda;
    }
    return '-';
  };

  const formatCurrencyDisplay = (quote) => {
    if (quote.valor !== undefined && quote.valor !== null) {
      return Number(quote.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return quote.valor_str || '0,00';
  };

  // Filtragem e ordenação
  const filteredQuotes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return quotes
      .filter(q => {
        // Filtro por termo de busca (número, cliente, data, valor ou nome do arquivo)
        if (term) {
          const numero = String(q.numero || '').toLowerCase();
          const cliente = String(q.cliente || '').toLowerCase();
          const dataStr = String(q.data_venda_str || '').toLowerCase();
          const valorStr = String(q.valor_str || '').toLowerCase();
          const arquivo = String(q.arquivo || '').toLowerCase();

          const matchesSearch =
            numero.includes(term) ||
            `#${numero}`.includes(term) ||
            cliente.includes(term) ||
            dataStr.includes(term) ||
            valorStr.includes(term) ||
            arquivo.includes(term);

          if (!matchesSearch) return false;
        }

        // Filtro por ano
        if (selectedYear) {
          const qYear = q.data_venda
            ? q.data_venda.substring(0, 4)
            : (q.data_venda_str ? q.data_venda_str.split('/')[2] : '');
          if (qYear !== selectedYear) return false;
        }

        // Filtro por intervalo de datas
        if (startDate && q.data_venda && q.data_venda < startDate) {
          return false;
        }
        if (endDate && q.data_venda && q.data_venda > endDate) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'recente') {
          const dateA = a.data_venda || '';
          const dateB = b.data_venda || '';
          if (dateB !== dateA) return dateB.localeCompare(dateA);
          return Number(b.numero || 0) - Number(a.numero || 0);
        }
        if (sortBy === 'antigo') {
          const dateA = a.data_venda || '';
          const dateB = b.data_venda || '';
          if (dateA !== dateB) return dateA.localeCompare(dateB);
          return Number(a.numero || 0) - Number(b.numero || 0);
        }
        if (sortBy === 'numero_desc') {
          return Number(b.numero || 0) - Number(a.numero || 0);
        }
        if (sortBy === 'numero_asc') {
          return Number(a.numero || 0) - Number(b.numero || 0);
        }
        if (sortBy === 'maior_valor') {
          return Number(b.valor || 0) - Number(a.valor || 0);
        }
        if (sortBy === 'menor_valor') {
          return Number(a.valor || 0) - Number(b.valor || 0);
        }
        if (sortBy === 'cliente_az') {
          return String(a.cliente || '').localeCompare(String(b.cliente || ''), 'pt-BR');
        }
        if (sortBy === 'cliente_za') {
          return String(b.cliente || '').localeCompare(String(a.cliente || ''), 'pt-BR');
        }
        return 0;
      });
  }, [quotes, searchTerm, selectedYear, startDate, endDate, sortBy]);

  const totalPages = Math.ceil(filteredQuotes.length / itemsPerPage) || 1;
  const paginatedQuotes = filteredQuotes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = searchTerm || selectedYear || startDate || endDate;

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedYear('');
    setStartDate('');
    setEndDate('');
    setSortBy('recente');
  };

  return (
    <div className="page-container animate-fade-in">
      {/* CABEÇALHO */}
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <History size={26} color="var(--primary-color)" />
            Orçamentos Retroativos
          </h1>
          <p className="page-subtitle">
            Acervo histórico de orçamentos do sistema antigo para consulta e download de PDFs
          </p>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          backgroundColor: '#ffffff',
          border: '1px solid var(--border-color)',
          borderRadius: '10px',
          padding: '0.6rem 1rem',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            backgroundColor: 'var(--primary-light)',
            color: 'var(--primary-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '700'
          }}>
            <FileText size={18} />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '500' }}>
              Orçamentos Arquivados
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: '700', color: 'var(--text-main)' }}>
              {filteredQuotes.length.toLocaleString('pt-BR')} {filteredQuotes.length !== quotes.length && `de ${quotes.length.toLocaleString('pt-BR')}`}
            </div>
          </div>
        </div>
      </div>

      {/* CARD PRINCIPAL */}
      <div className="card list-card">
        {/* BARRA DE BUSCA E FILTROS */}
        <div
          className="card-header"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.85rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid var(--border-color)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {/* Input de Busca */}
            <div className="search-bar" style={{ flex: '1', minWidth: '280px', maxWidth: '460px' }}>
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar por Nº do orçamento, cliente, data ou valor..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Controles de Filtro e Ordenação */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              {/* Filtro por Ano */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <Filter size={15} className="text-muted" />
                <select
                  className="standard-input"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{ width: 'auto', height: '36px', padding: '0 2rem 0 0.65rem', fontSize: '0.8rem' }}
                  title="Filtrar por Ano"
                >
                  <option value="">Todos os Anos</option>
                  {availableYears.map(yr => (
                    <option key={yr} value={yr}>Ano {yr}</option>
                  ))}
                </select>
              </div>

              {/* Filtro de Data Inicial / Final */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '600' }}>De:</span>
                <input
                  type="date"
                  className="standard-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ width: 'auto', height: '36px', padding: '0 0.5rem', fontSize: '0.8rem' }}
                />
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '600' }}>Até:</span>
                <input
                  type="date"
                  className="standard-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ width: 'auto', height: '36px', padding: '0 0.5rem', fontSize: '0.8rem' }}
                />
              </div>

              {/* Ordenação */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <ArrowUpDown size={15} className="text-muted" />
                <select
                  className="standard-input"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{ width: 'auto', height: '36px', padding: '0 2rem 0 0.65rem', fontSize: '0.8rem' }}
                >
                  <option value="recente">Mais recente</option>
                  <option value="antigo">Mais antigo</option>
                  <option value="numero_desc">Nº Orçamento (Maior → Menor)</option>
                  <option value="numero_asc">Nº Orçamento (Menor → Maior)</option>
                  <option value="maior_valor">Maior valor</option>
                  <option value="menor_valor">Menor valor</option>
                  <option value="cliente_az">Cliente (A → Z)</option>
                  <option value="cliente_za">Cliente (Z → A)</option>
                </select>
              </div>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="btn-outline"
                  style={{
                    height: '36px',
                    padding: '0 0.75rem',
                    fontSize: '0.78rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    color: '#dc2626',
                    borderColor: '#fca5a5',
                    backgroundColor: '#fef2f2'
                  }}
                >
                  <X size={14} /> Limpar Filtros
                </button>
              )}
            </div>
          </div>
        </div>

        {/* TABELA DE ORÇAMENTOS RETROATIVOS */}
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '130px' }}>Nº Orçamento</th>
                <th style={{ width: '150px' }}>Data da Venda</th>
                <th>Cliente</th>
                <th style={{ width: '170px' }}>Valor Total</th>
                <th style={{ width: '190px' }}>Arquivo</th>
                <th style={{ minWidth: '220px', maxWidth: '300px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                    Carregando acervo de orçamentos retroativos...
                  </td>
                </tr>
              ) : paginatedQuotes.length > 0 ? (
                paginatedQuotes.map((quote) => (
                  <tr key={quote.id || quote.numero}>
                    {/* Nº ORÇAMENTO */}
                    <td>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          backgroundColor: '#f3e8ff',
                          color: '#7c3aed',
                          border: '1px solid #d8b4fe',
                          padding: '0.2rem 0.6rem',
                          borderRadius: '6px',
                          fontWeight: '700',
                          fontSize: '0.82rem'
                        }}
                      >
                        #{quote.numero}
                      </span>
                    </td>

                    {/* DATA DA VENDA */}
                    <td>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-main)', fontWeight: '500' }}>
                        <Calendar size={14} className="text-muted" />
                        {formatDateDisplay(quote)}
                      </div>
                    </td>

                    {/* CLIENTE */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            backgroundColor: '#f1f5f9',
                            color: '#475569',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <User size={14} />
                        </div>
                        <span style={{ fontWeight: '600', color: 'var(--text-main)' }}>
                          {quote.cliente}
                        </span>
                      </div>
                    </td>

                    {/* VALOR */}
                    <td>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontWeight: '600', color: '#0f172a' }}>
                        <CreditCard size={14} className="text-muted" />
                        R$ {formatCurrencyDisplay(quote)}
                      </div>
                    </td>

                    {/* ARQUIVO */}
                    <td>
                      <span
                        style={{
                          fontSize: '0.78rem',
                          color: 'var(--text-muted)',
                          fontFamily: 'monospace',
                          backgroundColor: '#f8fafc',
                          padding: '0.2rem 0.45rem',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0'
                        }}
                      >
                        {quote.arquivo}
                      </span>
                    </td>

                    {/* AÇÕES */}
                    <td style={{ minWidth: '220px', maxWidth: '300px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                        {/* Visualizar na tela (Modal) */}
                        <button
                          type="button"
                          onClick={() => setPreviewQuote(quote)}
                          title="Visualizar orçamento na tela"
                          style={{
                            height: '28px',
                            padding: '0 0.55rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            borderRadius: '6px',
                            border: '1px solid #c084fc',
                            backgroundColor: '#f3e8ff',
                            color: '#7c3aed',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer'
                          }}
                        >
                          <Eye size={13} /> Visualizar
                        </button>

                        {/* Abrir em Nova Guia */}
                        <button
                          type="button"
                          onClick={() => window.open(`${API_BASE_URL}/orcamentos_retroativos.php?action=view&id=${quote.id}`, '_blank')}
                          title="Abrir PDF em uma nova aba"
                          style={{
                            height: '28px',
                            padding: '0 0.55rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            borderRadius: '6px',
                            border: '1px solid #38bdf8',
                            backgroundColor: '#e0f2fe',
                            color: '#0284c7',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer'
                          }}
                        >
                          <ExternalLink size={13} /> Nova Guia
                        </button>

                        {/* Baixar PDF */}
                        <a
                          href={`${API_BASE_URL}/orcamentos_retroativos.php?action=download&id=${quote.id}`}
                          download={quote.arquivo}
                          title="Baixar arquivo PDF"
                          style={{
                            height: '28px',
                            padding: '0 0.5rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            backgroundColor: '#f8fafc',
                            color: '#334155',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            textDecoration: 'none'
                          }}
                        >
                          <Download size={13} /> PDF
                        </a>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                    Nenhum orçamento retroativo encontrado para os filtros informados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* RODAPÉ DE PAGINAÇÃO */}
        {filteredQuotes.length > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border-color)',
              flexWrap: 'wrap',
              gap: '0.75rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Exibindo <strong>{((currentPage - 1) * itemsPerPage) + 1}</strong> a{' '}
                <strong>{Math.min(currentPage * itemsPerPage, filteredQuotes.length)}</strong> de{' '}
                <strong>{filteredQuotes.length.toLocaleString('pt-BR')}</strong> orçamentos
              </span>

              <select
                className="standard-input"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                style={{ width: 'auto', height: '32px', padding: '0 1.8rem 0 0.5rem', fontSize: '0.78rem' }}
              >
                <option value={15}>15 por página</option>
                <option value={30}>30 por página</option>
                <option value={50}>50 por página</option>
                <option value={100}>100 por página</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <button
                className="btn-outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
                style={{
                  padding: '0.35rem 0.65rem',
                  fontSize: '0.8rem',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                Primeira
              </button>
              <button
                className="btn-outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.8rem',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                Anterior
              </button>

              <span style={{ fontSize: '0.85rem', fontWeight: '600', padding: '0 0.6rem', color: 'var(--text-main)' }}>
                Página {currentPage} de {totalPages}
              </span>

              <button
                className="btn-outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.8rem',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Próxima
              </button>
              <button
                className="btn-outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
                style={{
                  padding: '0.35rem 0.65rem',
                  fontSize: '0.8rem',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Última
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL DE VISUALIZAÇÃO DO PDF */}
      <Modal
        isOpen={!!previewQuote}
        onClose={() => setPreviewQuote(null)}
        title={
          previewQuote
            ? `Orçamento Retroativo #${previewQuote.numero} — ${previewQuote.cliente}`
            : 'Visualizar Orçamento'
        }
        width="82vw"
        maxWidth="1100px"
        style={{ height: '86vh' }}
      >
        {previewQuote && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', height: '100%', flex: 1 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.75rem',
                padding: '0.6rem 0.85rem',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                <span>
                  <strong>Nº:</strong> #{previewQuote.numero}
                </span>
                <span>
                  <strong>Data:</strong> {formatDateDisplay(previewQuote)}
                </span>
                <span>
                  <strong>Cliente:</strong> {previewQuote.cliente}
                </span>
                <span>
                  <strong>Valor:</strong> R$ {formatCurrencyDisplay(previewQuote)}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => window.open(`${API_BASE_URL}/orcamentos_retroativos.php?action=view&id=${previewQuote.id}`, '_blank')}
                  style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                >
                  <ExternalLink size={14} /> Abrir em Nova Guia
                </button>
                <a
                  href={`${API_BASE_URL}/orcamentos_retroativos.php?action=download&id=${previewQuote.id}`}
                  download={previewQuote.arquivo}
                  className="btn-primary"
                  style={{ width: 'auto', padding: '0.35rem 0.85rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', textDecoration: 'none' }}
                >
                  <Download size={14} /> Baixar PDF
                </a>
              </div>
            </div>

            <div style={{ flex: 1, minHeight: '450px', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#525659' }}>
              <iframe
                title={`Orçamento ${previewQuote.numero}`}
                src={`${API_BASE_URL}/orcamentos_retroativos.php?action=view&id=${previewQuote.id}`}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

